/**
 * Safari-specific fixes for the task system
 * Addresses rendering issues in Safari 18.5+
 */

import { loadSafariStyles, isSafariBrowser } from './safari-style-loader';

export function applySafariFixes() {
  // Use improved Safari detection
  if (!isSafariBrowser()) {
    console.log('Not Safari browser, skipping fixes');
    return;
  }

  console.log('Applying Safari-specific fixes...');
  
  // Load Safari-specific styles
  loadSafariStyles();

  // Fix 1: Handle vh units properly in Safari
  // Safari has issues with 100vh including the URL bar
  function setViewportHeight() {
    const vh = window.innerHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  }

  setViewportHeight();
  window.addEventListener('resize', setViewportHeight);
  window.addEventListener('orientationchange', setViewportHeight);

  // Fix 2: Force repaint for flexbox containers and fix flex: 1 issues
  // Safari sometimes doesn't render flexbox children correctly
  function forceFlexboxRepaint() {
    const flexContainers = document.querySelectorAll(
      '.projects-container, .projects-content, .board-view-container, .tab-container, .kanban-wrapper, .tab-content'
    );
    
    flexContainers.forEach(container => {
      if (container) {
        // Force a repaint by modifying a property and reverting it
        const display = container.style.display;
        container.style.display = 'none';
        container.offsetHeight; // Trigger reflow
        container.style.display = display || '';
        
        // Fix flex: 1 issues in column direction
        const computedStyle = window.getComputedStyle(container);
        if (computedStyle.flexDirection === 'column' && computedStyle.flex === '1 1 0%') {
          container.style.flex = '1 1 auto';
          container.style.minHeight = '0';
        }
      }
    });
  }
  
  // Fix flex: 1 in column containers
  function fixFlexColumn() {
    // Find all elements with flex: 1 in column containers
    const allElements = document.querySelectorAll('*');
    allElements.forEach(el => {
      const style = window.getComputedStyle(el);
      if (style.display === 'flex' && style.flexDirection === 'column') {
        // Check children for flex: 1
        Array.from(el.children).forEach(child => {
          const childStyle = window.getComputedStyle(child);
          if (childStyle.flex === '1 1 0%' || childStyle.flex === '1') {
            child.style.flex = '1 1 auto';
            child.style.minHeight = '0';
          }
        });
      }
    });
  }
  
  // Fix tab navigation height issue
  function fixTabNav() {
    // Fix tab-nav elements with height: 100%
    const tabNavs = document.querySelectorAll('.tab-nav, [class*="tab-nav"]');
    tabNavs.forEach(nav => {
      const style = window.getComputedStyle(nav);
      if (style.height === '100%' || nav.style.height === '100%') {
        nav.style.height = 'auto';
        nav.style.minHeight = '48px';
        nav.style.alignItems = 'center';
        console.log('Fixed tab-nav height issue');
      }
    });
    
    // Fix any flex container with height: 100% that's not column direction
    const flexContainers = document.querySelectorAll('[style*="display: flex"], [style*="display:flex"], .flex, .d-flex');
    flexContainers.forEach(container => {
      const style = window.getComputedStyle(container);
      if (style.display === 'flex' && 
          style.flexDirection !== 'column' && 
          (style.height === '100%' || container.style.height === '100%')) {
        container.style.height = 'auto';
        container.style.maxHeight = '100%';
      }
    });
  }
  
  fixTabNav();
  
  fixFlexColumn();

  // Apply fix after DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => {
      forceFlexboxRepaint();
      fixFlexColumn();
      fixTabNav();
    });
  } else {
    setTimeout(() => {
      forceFlexboxRepaint();
      fixFlexColumn();
      fixTabNav();
    }, 100);
  }

  // Fix 3: Handle sticky positioning
  // Safari needs explicit height values for sticky containers
  function fixStickyPositioning() {
    const stickyElements = document.querySelectorAll(
      '.projects-header, .board-header, .tab-header, [style*="position: sticky"]'
    );
    
    stickyElements.forEach(element => {
      if (element && !element.style.position) {
        element.style.position = '-webkit-sticky';
        element.style.position = 'sticky';
      }
    });
  }

  fixStickyPositioning();

  // Fix 4: Handle overflow scrolling
  // Ensure smooth scrolling on touch devices
  function fixOverflowScrolling() {
    const scrollContainers = document.querySelectorAll(
      '.projects-content, .tab-content, .kanban-wrapper, .kanban-column-tasks'
    );
    
    scrollContainers.forEach(container => {
      if (container) {
        container.style.webkitOverflowScrolling = 'touch';
        
        // Fix for Safari's momentum scrolling issues
        if ('ontouchstart' in window) {
          container.addEventListener('touchstart', function() {
            this.style.overflow = 'auto';
          });
        }
      }
    });
  }

  fixOverflowScrolling();

  // Fix 5: Handle CSS Grid issues
  // Safari has issues with grid-template-columns auto-fill/auto-fit
  function fixGridLayouts() {
    const gridContainers = document.querySelectorAll('.grid-container, .projects-grid');
    
    gridContainers.forEach(container => {
      if (container) {
        const computedStyle = window.getComputedStyle(container);
        if (computedStyle.display === 'grid') {
          // Force grid recalculation
          container.style.display = 'block';
          setTimeout(() => {
            container.style.display = 'grid';
          }, 0);
        }
      }
    });
  }

  fixGridLayouts();

  // Fix 6: Handle flexbox gap fallback
  // Older Safari versions don't support gap in flexbox
  function addFlexGapFallback() {
    // Check if gap is supported
    const testElement = document.createElement('div');
    testElement.style.display = 'flex';
    testElement.style.gap = '1px';
    
    if (!testElement.style.gap) {
      // Gap not supported, add margins as fallback
      const flexWithGap = document.querySelectorAll('[style*="gap:"], .task-date, .header-actions');
      
      flexWithGap.forEach(element => {
        const children = element.children;
        for (let i = 1; i < children.length; i++) {
          children[i].style.marginLeft = '0.5rem';
        }
      });
    }
  }

  addFlexGapFallback();

  // Fix 7: Monitor for dynamic content changes
  // Re-apply fixes when content is dynamically loaded
  const observer = new MutationObserver((mutations) => {
    let shouldReapplyFixes = false;
    
    mutations.forEach(mutation => {
      if (mutation.type === 'childList' && mutation.addedNodes.length > 0) {
        mutation.addedNodes.forEach(node => {
          if (node.nodeType === 1 && (
            node.classList?.contains('projects-content') ||
            node.classList?.contains('board-view-container') ||
            node.classList?.contains('tab-content')
          )) {
            shouldReapplyFixes = true;
          }
        });
      }
    });
    
    if (shouldReapplyFixes) {
      setTimeout(() => {
        forceFlexboxRepaint();
        fixStickyPositioning();
        fixOverflowScrolling();
        fixGridLayouts();
      }, 100);
    }
  });

  // Start observing after a delay to ensure Vue has mounted
  setTimeout(() => {
    observer.observe(document.body, {
      childList: true,
      subtree: true
    });
  }, 1000);

  // Fix 8: Handle Safari-specific CSS custom properties
  document.documentElement.style.setProperty('--safari-fix', '1');
  
  // Add Safari-specific class to body for CSS targeting
  document.body.classList.add('safari-browser');
  
  console.log('Safari fixes applied successfully');
}

// Auto-apply fixes when module is imported
if (typeof window !== 'undefined') {
  applySafariFixes();
  
  // Re-apply on route changes (for SPAs)
  window.addEventListener('popstate', () => {
    setTimeout(applySafariFixes, 100);
  });
}